import DOMPurify from "dompurify";
import { Rocket } from "lucide-react";
import { icons } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface P2BannerProps {
  onCtaClick: () => void;
}

interface BannerCard {
  id: string;
  value: string;
  label: string;
  icon: string;
  display_order: number;
}

interface BannerData {
  id: string;
  active: boolean;
  badge_text: string;
  title: string;
  subtitle: string;
  description: string;
  cta_text: string;
  cta_description: string;
  cta_url: string | null;
  display_order: number;
  cards: BannerCard[];
}

interface CarouselSettings {
  autoplay_enabled: boolean;
  autoplay_interval: number;
}

const DynamicIcon = ({ name, className }: { name: string; className?: string }) => {
  // Convert kebab-case to PascalCase for lucide icons lookup
  const pascalName = name
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
  const IconComp = (icons as Record<string, any>)[pascalName];
  if (!IconComp) return <Rocket className={className} />;
  return <IconComp className={className} />;
};

export const P2Banner = ({ onCtaClick }: P2BannerProps) => {
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [settings, setSettings] = useState<CarouselSettings>({ autoplay_enabled: true, autoplay_interval: 5000 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const [bannersRes, cardsRes, settingsRes] = await Promise.all([
        supabase.from("cms_p2_banner").select("*").eq("active", true).order("display_order", { ascending: true }),
        supabase.from("cms_p2_banner_cards").select("*").order("display_order", { ascending: true }),
        supabase.from("cms_banner_carousel_settings").select("*").eq("id", "00000000-0000-0000-0000-000000000001").single(),
      ]);

      if (bannersRes.data && cardsRes.data) {
        const bannersWithCards = bannersRes.data.map((b: any) => ({
          ...b,
          cards: cardsRes.data.filter((c: any) => c.banner_id === b.id),
        }));
        setBanners(bannersWithCards);
      }
      if (settingsRes.data) setSettings(settingsRes.data);
    };
    fetchData();
  }, []);

  const goToNext = useCallback(() => {
    if (banners.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
      setIsTransitioning(false);
    }, 400);
  }, [banners.length]);

  useEffect(() => {
    if (!settings.autoplay_enabled || banners.length <= 1) return;
    intervalRef.current = setInterval(goToNext, settings.autoplay_interval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [settings.autoplay_enabled, settings.autoplay_interval, goToNext, banners.length]);

  if (banners.length === 0) return null;

  const data = banners[currentIndex];
  const count = data.cards.length;
  const gridColsClass =
    count === 1
      ? "sm:grid-cols-1 md:grid-cols-1"
      : count === 2
      ? "sm:grid-cols-2 md:grid-cols-2"
      : count === 3
      ? "sm:grid-cols-2 md:grid-cols-3"
      : "sm:grid-cols-2 md:grid-cols-4";
  const maxWidthClass =
    count === 1 ? "max-w-sm" : count === 2 ? "max-w-2xl" : count === 3 ? "max-w-4xl" : "max-w-5xl";

  return (
    <section id="banner" className="py-20 md:py-28 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 relative overflow-hidden">
      <div className="absolute top-10 right-10 w-32 h-32 bg-secondary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div className={`max-w-5xl mx-auto transition-opacity duration-400 ${isTransitioning ? "opacity-0" : "opacity-100"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-secondary/15 backdrop-blur-sm px-4 py-2 rounded-full border border-secondary/30">
              <Rocket className="h-4 w-4 text-secondary" />
              <span className="text-sm font-medium text-foreground">{data.badge_text}</span>
            </div>
          </div>

          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">{data.title}</h2>
            <p
              className="text-xl md:text-2xl text-muted-foreground mb-2"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  data.subtitle.replace(/<strong>(.*?)<\/strong>/g, '<span class="text-secondary font-bold">$1</span>')
                ),
              }}
            />
            <p className="text-lg text-muted-foreground">{data.description}</p>
          </div>

          {data.cards.length > 0 && (
            <div className={`grid grid-cols-1 ${gridColsClass} gap-4 md:gap-6 mb-10 mx-auto ${maxWidthClass}`}>
              {data.cards.map((card) => (
                <div key={card.id} className="text-center p-5 md:p-6 bg-background/50 backdrop-blur-sm rounded-2xl border border-border/50">
                  <DynamicIcon name={card.icon} className="h-7 w-7 md:h-8 md:w-8 text-secondary mx-auto mb-2 md:mb-3" />
                  <div className="text-xl md:text-2xl font-bold text-foreground mb-1">{card.value}</div>
                  <div className="text-sm text-muted-foreground">{card.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center">
            {data.cta_url ? (
              <a href={data.cta_url} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg hover:shadow-xl h-14 px-10 text-lg rounded-full group"
                >
                  {data.cta_text}
                  <Rocket className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
            ) : (
              <Button
                size="lg"
                onClick={onCtaClick}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg hover:shadow-xl h-14 px-10 text-lg rounded-full group"
              >
                {data.cta_text}
                <Rocket className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            )}
            <p className="text-sm text-muted-foreground mt-4">{data.cta_description}</p>
          </div>

          {banners.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setCurrentIndex(i);
                      setIsTransitioning(false);
                    }, 400);
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    if (settings.autoplay_enabled) {
                      intervalRef.current = setInterval(goToNext, settings.autoplay_interval);
                    }
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    i === currentIndex ? "bg-secondary w-6" : "bg-secondary/30 hover:bg-secondary/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
